import { useEffect, useRef, useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSButton from "../../system-design/DSButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSAlert from "../../system-design/DSAlert";
import ConfirmationModal from "../../ConfirmationModal";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import useFileUpload from "../../Gifts/GiftOrders/hooks/useFileUpload";
import { ContentProfile } from "../../../api/admin";
import { extractApiErrorMessage } from "../../../utils/util";
import {
  useContentProfileGift,
  useDeleteContentProfileGift,
  useUpsertContentProfileGift,
} from "../hooks/useContentProfiles";

interface ProfileGiftCardProps {
  profile: ContentProfile;
}

const GREETING_MAX_LENGTH = 160;
const DEFAULT_SENDER_IMAGE_URL =
  "https://res.cloudinary.com/klydoclock/image/upload/profile/defaultPhoto_mc2iv6.png";

/**
 * Bulk-gifts the same greeting to every device currently assigned to this
 * profile — e.g. an HR buyer purchasing clocks for a team, wanting the same
 * welcome greeting on all of them. A gift lives in its own Mongo document
 * (GiftService), delivered device-initiated on the clock's own onboarding
 * flow — entirely separate from this profile's Firestore sync(), so this
 * card has its own independent Save, not part of useProfileDraft/the
 * Settings tab's unified Save button (matching how DeviceModal's own cards
 * each save independently, not through one shared button either). Informed
 * by GiftOrderModal.tsx's field set, not extracted from it.
 */
const ProfileGiftCard = ({ profile }: ProfileGiftCardProps) => {
  const { data: gift, isLoading } = useContentProfileGift(profile.id);
  const { mutate: save, isPending: isSaving } = useUpsertContentProfileGift(
    profile.id,
  );
  const { mutate: remove, isPending: isDeleting } = useDeleteContentProfileGift(
    profile.id,
  );

  const [klydoId, setKlydoId] = useState("");
  const [greetingContent, setGreetingContent] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderImageUrl, setSenderImageUrl] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setKlydoId(gift?.klydo?.id ?? "");
    setGreetingContent(gift?.greetingContent ?? "");
    setSenderName(gift?.senderName ?? "");
    setSenderImageUrl(gift?.senderImageUrl ?? "");
  }, [gift]);

  const { handleFileSelection, isPending: isUploading } = useFileUpload({
    onError: () => setError("Failed to upload image. Please try again."),
    onSuccess: (url) => setSenderImageUrl(url),
  });

  const hasNoDevices = !profile.clockIds.length;
  const canSave =
    !!klydoId &&
    !!greetingContent.trim() &&
    !!senderName.trim() &&
    !hasNoDevices;

  const handleSave = () => {
    setError(null);
    save(
      {
        klydoId,
        greetingContent: greetingContent.trim(),
        senderName: senderName.trim(),
        senderImageUrl: senderImageUrl || undefined,
      },
      {
        onError: (e) =>
          setError(extractApiErrorMessage(e, "Failed to save gift")),
      },
    );
  };

  return (
    <DSCard sx={{ pb: "15px", mb: 2, bgcolor: "background.paper" }}>
      <DSTypography variant="h6" sx={{ px: 2, pt: 2 }}>
        Gift
      </DSTypography>
      <DSBox
        sx={{ borderBottom: "1px solid", borderColor: "divider", mt: 1 }}
      />
      <DSCardContent>
        <DSTypography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          Linked to every device currently assigned to this profile — delivered
          when each clock first onboards, independent of Sync. Save again after
          changing devices on the Devices tab to re-link.
        </DSTypography>

        {hasNoDevices && (
          <DSAlert severity="warning" sx={{ mb: 2 }}>
            Assign at least one device to this profile before creating a gift.
          </DSAlert>
        )}

        {isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <DSCircularProgress size={20} />
          </DSBox>
        ) : (
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {gift && (
              <DSTypography variant="caption" color="text.secondary">
                Currently linked to {gift.clocksIds?.length ?? 0} device(s) as
                of the last save.
              </DSTypography>
            )}

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSTextField
                label="Klydo"
                size="small"
                value={klydoId || "No klydo selected"}
                disabled
                fullWidth
              />
              <DSButton
                variant="outlined"
                size="small"
                onClick={() => setShowPicker(true)}
              >
                Pick
              </DSButton>
            </DSBox>

            <DSTextField
              label="Sender name"
              size="small"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />

            <DSTextField
              label="Greeting"
              size="small"
              multiline
              rows={3}
              value={greetingContent}
              onChange={(e) => setGreetingContent(e.target.value)}
              helperText={`${greetingContent.length}/${GREETING_MAX_LENGTH}`}
              slotProps={{
                htmlInput: { maxLength: GREETING_MAX_LENGTH },
              }}
            />

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={senderImageUrl || DEFAULT_SENDER_IMAGE_URL}
                alt="Sender"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  cursor: "pointer",
                  border: "2px dashed gray",
                }}
                onClick={() => fileInputRef.current?.click()}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleFileSelection}
              />
              <DSTypography variant="caption" color="text.secondary">
                {isUploading ? "Uploading..." : "Click to change sender image"}
              </DSTypography>
            </DSBox>

            {error && (
              <DSTypography variant="caption" color="error">
                {error}
              </DSTypography>
            )}

            <DSBox sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <DSPrimaryButton
                disabled={!canSave || isSaving}
                onClick={handleSave}
              >
                {isSaving ? "Saving..." : gift ? "Save gift" : "Create gift"}
              </DSPrimaryButton>
              {gift && (
                <DSErrorButton
                  disabled={isDeleting}
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ minWidth: "120px" }}
                >
                  Delete gift
                </DSErrorButton>
              )}
            </DSBox>
          </DSBox>
        )}
      </DSCardContent>

      {showPicker && (
        <KlydosPickDialog
          onClose={() => setShowPicker(false)}
          selectedKlydos={klydoId ? [klydoId] : []}
          limit={1}
          greet
          onPickKlydos={(picked) => {
            setKlydoId(picked[0] ?? "");
            setShowPicker(false);
          }}
        />
      )}

      <ConfirmationModal
        open={showDeleteConfirm}
        message="Delete this gift? It will no longer be delivered to any device, even ones already assigned to this profile."
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          remove(undefined, { onSuccess: () => setShowDeleteConfirm(false) });
        }}
      />
    </DSCard>
  );
};

export default ProfileGiftCard;
